/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
} from '@mui/material'
import { useOrgan } from '../../../../hooks/useOrgan'
import { organizationPrimaryColors } from '../../../../layouts/PermanentSidebarMUI'
import { useMutation } from '@tanstack/react-query'
import { entities } from '../../../../constants/entities'
import useBaseService from '../../../../hooks/services/useBaseService'
import { useNavigate } from 'react-router-dom'
import useLoader from '../../../../hooks/useLoader'
import useRemoteSelectOptions from '../../../../hooks/useRemoteSelectOptions'
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from '../../../../utils/objectUtils'
import useForm from '../../../../hooks/useForm'
import { faClipboardCheck } from '@fortawesome/free-solid-svg-icons'
import createField from '../../../../utils/factory/createField'
import { useCallback, useEffect, useRef } from 'react'
import SimpleBody from '../../../../components/forms/SimpleBody'

const reportFields = [
  {
    section: 'Romaneio',
    fields: [
      createField({
        id: 'report.recipient_customer_id',
        label: 'Cliente Destinatário',
        type: 'select',
        placeholder: 'Selecione o cliente',
        icon: faClipboardCheck,
        isClearable: true,
      }),
      createField({
        id: 'report.movement_type_id',
        label: 'Tipo Movimento',
        type: 'select',
        placeholder: 'Selecione o tipo de movimento',
        icon: faClipboardCheck,
        isClearable: true,
      }),
      createField({
        id: 'report.movement_id',
        label: 'Protocolo',
        type: 'select',
        placeholder: 'Selecione o movimento',
        icon: faClipboardCheck,
        fullWidth: true,
        isClearable: true,
      }),
    ],
  },
]

export const ExportInvoice = ({ data, open, onClose }) => {
  const navigate = useNavigate()
  const { get: baseServiceGet, formErrors } = useBaseService(navigate)
  const { showLoader, hideLoader } = useLoader()

  const [reportSection] = reportFields

  const { formData, handleChange, setFormData, resetForm } = useForm(
    setDefaultFieldValues([reportSection]),
    [reportSection]
  )

  const customerSelect = useRemoteSelectOptions({
    queryKey: ['customers'],
    baseServiceGet,
    entityPath: entities.shipments.customers(data?.id),
    fieldId: 'report.recipient_customer_id',
    formData,
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    immediateFetch: !!data?.id,
    setFormData,
    perPage: 15,
    enablePagination: true,
    refetchOnWindowFocus: true,
    withoutDeletedAt: true,
  })

  const movementTypeCustomerRef = useRef(customerSelect.selectedValue?.value)

  const movementTypesSelect = useRemoteSelectOptions({
    queryKey: ['movementTypes'],
    baseServiceGet,
    entityPath: entities.shipments.movementTypes(data?.id),
    fieldId: 'report.movement_type_id',
    formData,
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
    immediateFetch: !!data?.id,
    setFormData,
    perPage: 15,
    enablePagination: true,
    refetchOnWindowFocus: true,
    withoutDeletedAt: true,
    refetchWhen: () => {
      if (
        movementTypeCustomerRef.current !== customerSelect.selectedValue?.value
      ) {
        movementTypeCustomerRef.current = customerSelect.selectedValue?.value
        return true
      }

      return false
    },
  })

  const movementsSelect = useRemoteSelectOptions({
    queryKey: ['movements'],
    baseServiceGet,
    entityPath: entities.shipments.movements(data?.id),
    fieldId: 'report.movement_id',
    formData,
    isMulti: false,
    mapToOption: (item) => ({
      value: item.id,
      label: `P# - ${item.number}`,
    }),
    immediateFetch: !!data?.id,
    setFormData,
    perPage: 15,
    enablePagination: true,
    refetchOnWindowFocus: true,
    withoutDeletedAt: true,
  })

  const selects = {
    'report.recipient_customer_id': customerSelect,
    'report.movement_type_id': movementTypesSelect,
    'report.movement_id': movementsSelect,
  }

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const exportProtocol = useMutation({
    mutationFn: async ({ movementId }) => {
      showLoader()
      const params = {}
      const response = await baseServiceGet(
        entities.reports.packingList(data?.id),
        params,
        {
          movement_id: movementId,
        }
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        const url = response.result

        const link = document.createElement('a')
        link.href = url

        // Força o download
        link.setAttribute('download', '')

        // Abre em nova aba para disparar o download
        link.setAttribute('target', '_blank')

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        onClose()
        resetForm()
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const handleSubmit = async () => {
    try {
      await exportProtocol.mutateAsync({
        movementId: movementsSelect.selectedValue?.value,
      })
    } catch (error) {
      console.error('Erro ao exportar etiquetas:', {
        status: error.status,
        message: error.message,
      })
    }
  }

  const { selectedOrgan } = useOrgan()
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM

  useEffect(() => {
    if (!open) {
      resetForm()
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: primaryColor,
          color: '#FFFFFF',
        }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Imprimir Romaneio</DialogTitle>
      </Box>
      <DialogContent>
        <div className="container-fluid p-1">
          <SimpleBody
            fields={reportSection?.fields ?? []}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={getOptions}
            getSelectedValue={getSelectedValue}
            getSelectMeta={getSelectMeta}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          disabled={
            !movementsSelect.selectedValue?.value || exportProtocol.isPending
          }>
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  )
}
